import { Params } from '@angular/router';
import { AppConfig } from '@mkp/shared/util-app-config';
import {
  BOOST_SUCCESS,
  APPLICATIONS,
  APPLICATIONS_MOBILE_WITH_QUERY,
  BO_CLAIM_COMPANY,
  BOOST_VACANCY,
  CLAIM_MANAGEMENT,
  COMPANY_PROFILE_WITH_ID,
  CREATE_COMPANY,
  CREATE_VACANCY,
  CREATE_VACANCY_GROUP,
  DASHBOARD,
  DESIGN_SYSTEM,
  EDIT_VACANCY,
  EXTEND,
  getCreditManagementRoute,
  getPurchaseCreditsRoute,
  getPurchaseHistoryRoute,
  getVacancyListRoute,
  ME,
  MY_USER_PROFILE,
  PAY_PER_INVITE,
  PREVIEW_VACANCY,
  PUBLISH_VACANCY,
  PURCHASE_CREDITS,
  PURCHASE_CREDITS_COMPARISON,
  PURCHASE_HISTORY,
  SELECT_WORKSPACE,
  EDIT_VACANCY_PATTERN,
  PUBLISH_VACANCY_PATTERN,
  VACANCY_DETAIL_PATTERN,
  VACANCY_GROUP_FORM,
} from './layout-pages.helpers';

const { routes } = AppConfig;

interface GoBackConfig {
  route: string | ((workspaceId: string) => string);
  title: string;
}

const goBackDefault = (url: string): GoBackConfig => ({
  route: url,
  title: 'BACK',
});

const goBackMap: Record<string, GoBackConfig> = {
  [`${SELECT_WORKSPACE}-${ME}`]: {
    route: SELECT_WORKSPACE,
    title: 'WORKSPACE_SELECT.BACK_TO_WORKSPACE_SELECTION',
  },
  [`${BO_CLAIM_COMPANY}-${CREATE_COMPANY}`]: {
    route: CLAIM_MANAGEMENT,
    title: 'CLAIM_MANAGEMENT.GO_BACK',
  },
  [BOOST_SUCCESS]: {
    route: getVacancyListRoute,
    title: 'PUBLISHING_OPTIONS.BUTTON.BACK_TO_JOB_ADS',
  },
  [ME]: { route: DASHBOARD, title: 'DASHBOARD_PAGE.TITLE' },
  [PUBLISH_VACANCY]: {
    route: getVacancyListRoute,
    title: 'PUBLISHING_OPTIONS.BUTTON.BACK_TO_JOB_ADS',
  },
  [EDIT_VACANCY]: {
    route: getVacancyListRoute,
    title: 'PUBLISHING_OPTIONS.BUTTON.BACK_TO_JOB_ADS',
  },
  [PREVIEW_VACANCY]: {
    route: getVacancyListRoute,
    title: 'PUBLISHING_OPTIONS.BUTTON.BACK_TO_JOB_ADS',
  },
  [BOOST_VACANCY]: {
    route: getVacancyListRoute,
    title: 'PUBLISHING_OPTIONS.BUTTON.BACK_TO_JOB_AD',
  },
  [CLAIM_MANAGEMENT]: { route: CLAIM_MANAGEMENT, title: 'CLAIM_MANAGEMENT.GO_BACK' },
  [APPLICATIONS]: {
    route: getVacancyListRoute,
    title: 'PUBLISHING_OPTIONS.BUTTON.BACK_TO_JOB_ADS',
  },
  [APPLICATIONS_MOBILE_WITH_QUERY]: {
    route: getVacancyListRoute,
    title: 'APPLICATION.BUTTON.BACK_TO_ALL_APPLICATIONS_MOBILE',
  },
  [PURCHASE_CREDITS]: {
    route: getCreditManagementRoute,
    title: 'PURCHASE_CREDITS.GO_BACK',
  },
  [PURCHASE_HISTORY]: {
    route: getCreditManagementRoute,
    title: 'PURCHASE_CREDITS.GO_BACK',
  },
  [EXTEND]: {
    route: getPurchaseHistoryRoute,
    title: 'EXTEND_CREDIT.GO_BACK_TO_PURCHASE_HISTORY',
  },
  [PURCHASE_CREDITS_COMPARISON]: {
    route: getPurchaseCreditsRoute,
    title: 'PURCHASE_CREDITS.GO_BACK_TO_PURCHASE_CREDITS',
  },
  [VACANCY_GROUP_FORM]: {
    route: COMPANY_PROFILE_WITH_ID,
    title: 'VACANCY_GROUP_FORM.BUTTON.BACK_TO_COMPANY_PROFILE',
  },
  [CREATE_VACANCY]: {
    route: getVacancyListRoute,
    title: 'PUBLISHING_OPTIONS.BUTTON.BACK_TO_JOB_ADS',
  },
  [CREATE_VACANCY_GROUP]: {
    route: COMPANY_PROFILE_WITH_ID,
    title: 'VACANCY_GROUP_FORM.BUTTON.BACK_TO_COMPANY_PROFILE',
  },
  [DESIGN_SYSTEM]: {
    route: DASHBOARD,
    title: 'DASHBOARD',
  },
  [PAY_PER_INVITE]: {
    route: `/${routes.workspace}/:workspaceId/${routes.vacancy}/:id/${routes.applications}`,
    title: 'PAY_PER_INVITE.BUTTON.BACK_TO_APPLICATIONS',
  },
};

const createRouteHash = (prev: string | undefined, curr: string | undefined) =>
  shouldCheckPrevPage(curr) ? `${prev ? prev + '-' : ''}${curr ?? ''}` : (curr ?? '');

const mapUrlToPageName = (url: string, isMobile: boolean, queryParams: Params) => {
  switch (true) {
    case url.includes(MY_USER_PROFILE):
      return ME;
    case url.includes(SELECT_WORKSPACE):
      return SELECT_WORKSPACE;
    case EDIT_VACANCY_PATTERN.test(url):
      return EDIT_VACANCY;
    case PUBLISH_VACANCY_PATTERN.test(url):
      return PUBLISH_VACANCY;
    case url.includes(BOOST_SUCCESS):
      return BOOST_SUCCESS;
    case url.includes(BOOST_VACANCY):
      return BOOST_VACANCY;
    case url.includes(CREATE_COMPANY):
      return CREATE_COMPANY;
    case url.includes(BO_CLAIM_COMPANY):
      return BO_CLAIM_COMPANY;
    case url.includes(CLAIM_MANAGEMENT):
      return CLAIM_MANAGEMENT;
    case VACANCY_DETAIL_PATTERN.test(url):
      return PREVIEW_VACANCY;
    case url.includes(PAY_PER_INVITE):
      return PAY_PER_INVITE;
    case url.includes(APPLICATIONS): {
      return !isMobile
        ? APPLICATIONS
        : queryParams['id']
          ? APPLICATIONS_MOBILE_WITH_QUERY
          : APPLICATIONS;
    }
    case url.includes(PURCHASE_CREDITS_COMPARISON):
      return PURCHASE_CREDITS_COMPARISON;
    case url.includes(PURCHASE_CREDITS):
      return PURCHASE_CREDITS;
    case url.includes(PURCHASE_HISTORY):
      return PURCHASE_HISTORY;
    case url.includes(EXTEND):
      return EXTEND;
    case url.includes(CREATE_VACANCY_GROUP):
      return CREATE_VACANCY_GROUP;
    case url.includes(VACANCY_GROUP_FORM):
      return VACANCY_GROUP_FORM;
    case url.includes(CREATE_VACANCY):
      return CREATE_VACANCY;
    case url.includes(DESIGN_SYSTEM):
      return DESIGN_SYSTEM;
    default:
      return undefined;
  }
};

const PREV_AWARE_PAGES = [ME, CREATE_COMPANY];

const shouldCheckPrevPage = (curr: string | undefined): boolean =>
  !!curr && PREV_AWARE_PAGES.includes(curr);

const resolveRoute = (config: GoBackConfig, paramMap: Params): { route: string; title: string } => {
  const workspaceId = paramMap['workspaceId'];

  // If route is a function, call it with workspaceId
  if (typeof config.route === 'function') {
    return {
      route: workspaceId ? config.route(workspaceId) : '',
      title: config.title,
    };
  }

  // If route contains params like :workspaceId, replace them
  let route = config.route;
  if (route.includes(':')) {
    const slugMatches = route.match(/:\w+/g) || [];
    slugMatches.forEach((slug) => {
      const slugKey = slug.slice(1);
      const slugValue = paramMap[slugKey];
      if (slugValue !== undefined) {
        route = route.replace(slug, slugValue);
      }
    });
  }

  return { route, title: config.title };
};

export const getGoBackValue = (
  prevUrl: string,
  currentUrl: string,
  params: Params,
  queryParams: Params,
  isMobile: boolean
): { route: string; title: string } | undefined => {
  const routeHash = createRouteHash(
    mapUrlToPageName(prevUrl, isMobile, queryParams),
    mapUrlToPageName(currentUrl, isMobile, queryParams)
  );

  const config = goBackMap[routeHash] || goBackDefault(prevUrl);
  return resolveRoute(config, params);
};
